<script setup lang="ts">
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import BCard from 'src/shared/components/base/BCard/BCard.vue';
import { clearAllStorage } from 'src/shared/utilities/versionManager';
import {
  mdiDatabaseRefresh,
  mdiAlertCircle,
  mdiCheckCircle,
} from '@quasar/extras/mdi-v7';
import type { User } from 'src/modules/User/models/User';

const { t } = useI18n();
const $q = useQuasar();
const isClearing = ref(false);
interface Props {
  currentUser: User | null;
}

defineProps<Props>();

async function handleClearStorage() {
  isClearing.value = true;

  try {
    await clearAllStorage({
      clearServiceWorkers: true,
      clearCaches: true,
      clearIndexedDB: true,
      clearLocalStorage: false, // Keep auth token
      clearSessionStorage: true,
    });

    $q.notify({
      message: t('STORAGE_CLEARED_SUCCESS'),
      caption: t('STORAGE_CLEARED_SUCCESS_CAPTION'),
      color: 'positive',
      icon: mdiCheckCircle,
      position: 'top',
      timeout: 2000,
    });

    // Stop spinner before reload, then reload after showing notification
    isClearing.value = false;
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  } catch (error) {
    console.error('Failed to clear storage:', error);
    $q.notify({
      message: t('STORAGE_CLEARED_ERROR'),
      color: 'negative',
      icon: mdiAlertCircle,
      position: 'top',
      timeout: 3000,
    });
    isClearing.value = false;
  }
}
</script>

<template>
  <BCard
    size="sm"
    class="rounded-borders-xl"
    :class="$q.dark.isActive ? 'shadow-card-dark' : 'shadow-card bg-white'"
    :dark="$q.dark.isActive"
  >
    <q-card-section>
      <div class="row items-center q-gutter-sm">
        <q-icon
          :name="mdiDatabaseRefresh"
          size="md"
          color="warning"
        />
        <div class="text-h6">{{ t('CLEAR_STORAGE') }}</div>
      </div>
      <div class="text-caption text-muted q-mt-sm">
        {{ t('CLEAR_STORAGE_DESCRIPTION') }}
      </div>
    </q-card-section>

    <q-card-section class="q-pt-none">
      <q-banner
        dense
        rounded
        class="text-warning"
      >
        <template #avatar>
          <q-icon
            :name="mdiAlertCircle"
            color="warning"
          />
        </template>
        <div class="text-caption q-mb-md">
          {{ t('CLEAR_STORAGE_WARNING') }}
        </div>
        <template #action>
          <q-btn
            :loading="isClearing"
            :disable="isClearing"
            color="warning"
            text-color="dark"
            :label="t('CLEAR_STORAGE')"
            :icon="mdiDatabaseRefresh"
            class="full-width"
            no-caps
            unelevated
            @click="handleClearStorage"
          />
        </template>
      </q-banner>
    </q-card-section>
  </BCard>
</template>
